import React,{useState,useMemo,useEffect,useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {PageHeader,FilterBar,DataTable,Modal,Badge,Tabs} from '../components/ui/SharedUI';
import PropertyForm from '../components/forms/PropertyForm';
import {demoRepository} from '../repository/demoRepository';
import {calculations} from '../utils/calculations';
import {useFeedback} from '../components/ui/FeedbackUI';
import pocketbaseService from '../services/pocketbaseService';

const clamp=(value,min,max)=> Math.min(max,Math.max(min,Number(value) || 0));
const getSafeMetadata=(property)=> property?.metadata && typeof property.metadata==='object' && !Array.isArray(property.metadata) ? property.metadata : {};
const getCardImageDisplay=(property)=> {
  const saved=getSafeMetadata(property).cardImage || {};
  return {
    x: clamp(saved.x ?? 50,0,100),
    y: clamp(saved.y ?? 45,0,100),
    zoom: clamp(saved.zoom ?? 1,1,1.6)
  };
};
const getPropertyHeroUrl=(property)=> property?.propertyImageUrls?.[0] || property?.imageUrl || '';
const getPropertyThumbUrl=(property)=> property?.propertyImageThumbUrls?.[0] || property?.thumbnailUrl || property?.imageUrl || '';
const normaliseStatus=(value)=> String(value || '').trim().toLowerCase().replace(/\s+/g,'_');
const getPropertyFinancialDetails=(property)=> {
  const financial=property?.metadata?.financial || {};
  return {
    loanType: property?.loanType || financial.loanType || ''
  };
};
const propertyColumnHelp={
  'Interest Rate': 'Interest Rate is the recorded annual rate. When a property has multiple active linked loans, the table shows the balance-weighted rate across those loans.',
  'Monthly Installment': 'Monthly Installment is formula-driven from active loan balance, annual interest rate, repayment type, loan term and elapsed months. Active linked loan installments are summed. It is separate from recorded loan-payment cash flow.',
  'Anticipated Monthly Net Rent': 'Current anticipated rent after deductions that are actually recorded in the rent schedule. Each schedule is annualised using its payment frequency and divided by 12. Costs that are not recorded, including council rates, water, strata, insurance, maintenance or other ownership costs, are not invented or deducted.',
  'Indicative Cash Flow': 'High-level current projection: Anticipated Monthly Net Rent minus formula-driven Monthly Installment. The annual amount is 12 × the monthly result. Positive/negative labels apply to investment properties only. This is indicative, not a complete profitability or tax calculation, because unrecorded ownership costs are excluded.',
  'Agent Fees / Rate': 'Property-level agent fee calculation:\n\n• Monthly agent fees are the current recorded schedule fees, annualised using each payment frequency and divided by 12.\n• Effective rate equals total monthly agent fees divided by total monthly gross rent.\n\nIf different spaces have different rates, the percentage shown is the gross-rent-weighted effective rate.',
  'Recorded Cash Flow': 'Recorded cash-flow calculation:\n\n• all recorded income transactions\n• minus all recorded expense transactions\n• minus active insurance premiums\n• minus 12 × the recorded monthly loan-payment amount\n\nThis does not use Anticipated Monthly Net Rent or the formula-driven Monthly Installment. A $0.00 result usually means the relevant recorded transaction or loan-payment data is absent, or the recorded amounts net to zero.',
  'Equity': 'Equity equals current market value minus current loan balance. Active linked loan records are authoritative; the property financial balance is used only when no active linked loan exists.'
};
const isOwnerOccupiedProperty=(property,loanType)=> {
  const explicitUse=normaliseStatus(property?.propertyUse || property?.metadata?.propertyUse || property?.useType || property?.occupancyType || '');
  if (explicitUse) {
    return explicitUse.includes('owner_occupied')
      || explicitUse.includes('primary_residence')
      || explicitUse==='ppr';
  }
  return normaliseStatus(loanType).includes('owner_occupier');
};

const PropertiesPage=()=> {
  const navigate=useNavigate();
  const {showToast}=useFeedback();
  const [isAddModalOpen,setIsAddModalOpen]=useState(false);
  const [searchTerm,setSearchTerm]=useState('');
  const [statusFilter,setStatusFilter]=useState('Active');
  const [activeTab,setActiveTab]=useState('properties');
  const [refreshTrigger,setRefreshTrigger]=useState(0);
  const [uploadingImagePropertyId,setUploadingImagePropertyId]=useState(null);
  const [imageEditor,setImageEditor]=useState(null);
  const imageDragRef=useRef(null);

  useEffect(()=> {
    const handleRefresh=()=> setRefreshTrigger(prev=> prev + 1);
    window.addEventListener('mpp:data-refreshed',handleRefresh);
    return ()=> window.removeEventListener('mpp:data-refreshed',handleRefresh);
  },[]);

  const properties=useMemo(()=> {
    let list=demoRepository.getAllProperties();
    if (statusFilter !=='All') list=list.filter(p=> p.status===statusFilter);
    if (searchTerm) {
      const lower=searchTerm.toLowerCase();
      list=list.filter(p=> p.name.toLowerCase().includes(lower) || p.address.toLowerCase().includes(lower));
    }
    return list;
  },[searchTerm,statusFilter,refreshTrigger]);

  const activeProperties=useMemo(()=> {
    return demoRepository.getAllProperties().filter(p=> p.status==='Active' || p.status==='active');
  },[refreshTrigger]);

  const metricProperties=useMemo(()=> properties.filter(property=> !['archived','sold'].includes(normaliseStatus(property.status))),[properties]);

  const propertyRentMetricsById=useMemo(()=> (
    new Map(demoRepository.getAllProperties().map(property=> [
      String(property.id),
      calculations.getPropertyAnticipatedRentMetrics(property.id)
    ]))
  ),[refreshTrigger]);

  const portfolioRentMetrics=useMemo(()=> {
    const totals={
      anticipatedMonthlyGrossRent: 0,
      anticipatedMonthlyNetRent: 0,
      monthlyAgentFees: 0,
      monthlyFeeGrossRent: 0,
      rentIncompleteProperties: 0,
      feeIncompleteProperties: 0,
      rentalPropertyCount: 0
    };
    metricProperties.forEach(property=> {
      const metrics=propertyRentMetricsById.get(String(property.id));
      if (!metrics || metrics.recordCount <= 0) return;
      totals.rentalPropertyCount += 1;
      if (metrics.rentComplete) {
        totals.anticipatedMonthlyGrossRent += metrics.monthlyGrossRent;
        totals.anticipatedMonthlyNetRent += metrics.monthlyNetRent;
      } else totals.rentIncompleteProperties += 1;
      if (metrics.feeComplete) {
        totals.monthlyAgentFees += metrics.monthlyAgentFee;
        totals.monthlyFeeGrossRent += metrics.monthlyFeeGrossRent;
      } else {
        totals.feeIncompleteProperties += 1;
      }
    });
    return {
      ...totals,
      anticipatedMonthlyGrossRent: calculations.roundMoney(totals.anticipatedMonthlyGrossRent),
      anticipatedMonthlyNetRent: calculations.roundMoney(totals.anticipatedMonthlyNetRent),
      monthlyAgentFees: calculations.roundMoney(totals.monthlyAgentFees),
      effectiveAgentFeePercent: totals.feeIncompleteProperties===0 && totals.monthlyFeeGrossRent > 0
        ? calculations.roundMoney((totals.monthlyAgentFees/totals.monthlyFeeGrossRent)*100)
        : null
    };
  },[metricProperties,propertyRentMetricsById]);

  const portfolioLoanMetrics=useMemo(()=> (
    calculations.getPortfolioLoanMetrics(metricProperties.map(property=> property.id))
  ),[metricProperties,refreshTrigger]);

  const installmentSplit=useMemo(()=> {
    const result={
      ppr: {amount: 0,incomplete: 0,propertyCount: 0},
      investment: {amount: 0,incomplete: 0,propertyCount: 0}
    };
    metricProperties.forEach(property=> {
      const loanMetrics=calculations.getPropertyLoanMetrics(property.id);
      const financial=getPropertyFinancialDetails(property);
      const bucket=isOwnerOccupiedProperty(property,financial.loanType) ? result.ppr : result.investment;
      bucket.propertyCount += 1;
      bucket.amount += Number(loanMetrics.monthlyInstallment || 0);
      bucket.incomplete += Number(loanMetrics.incompleteLoanCount || 0);
    });
    result.ppr.amount=calculations.roundMoney(result.ppr.amount);
    result.investment.amount=calculations.roundMoney(result.investment.amount);
    return result;
  },[metricProperties,refreshTrigger]);

  const indicativePortfolioCashFlow=useMemo(()=> {
    const result={
      monthlyCashFlow: 0,
      annualCashFlow: 0,
      positive: 0,
      negative: 0,
      neutral: 0,
      incomplete: 0,
      investmentPropertyCount: 0
    };
    metricProperties.forEach(property=> {
      const financial=getPropertyFinancialDetails(property);
      if (isOwnerOccupiedProperty(property,financial.loanType)) return;
      result.investmentPropertyCount += 1;
      const position=calculations.getPropertyIndicativeCashFlowPosition(property.id);
      if (!position.complete || position.monthlyCashFlow===null) {
        result.incomplete += 1;
        return;
      }
      result.monthlyCashFlow += Number(position.monthlyCashFlow || 0);
      result.annualCashFlow += Number(position.annualCashFlow || 0);
      if (position.classification==='positive') result.positive += 1;
      else if (position.classification==='negative') result.negative += 1;
      else result.neutral += 1;
    });
    result.monthlyCashFlow=calculations.roundMoney(result.monthlyCashFlow);
    result.annualCashFlow=calculations.roundMoney(result.annualCashFlow);
    return result;
  },[metricProperties,refreshTrigger]);

  const activeAgents=useMemo(()=> {
    const agents=[];
    activeProperties.forEach(prop=> {
      const agent=prop.metadata?.agent || {};
      const hasAgent=prop.agentAgencyName || prop.agentManager || prop.agentEmail || prop.agentPhone || agent.agencyName || agent.managerName || agent.email || agent.phone;
      if (hasAgent) {
        agents.push({
          propertyId: prop.id,
          propertyName: prop.name,
          agencyName: prop.agentAgencyName || agent.agencyName || '',
          managerName: prop.agentManager || prop.agentName || agent.managerName || '',
          email: prop.agentEmail || agent.email || '',
          phone: prop.agentPhone || agent.phone || '',
          emergencyPhone: prop.agentEmergencyPhone || agent.emergencyPhone || '',
          statementEmail: prop.agentStatementEmail || agent.statementEmail || '',
          officeAddress: prop.agentOfficeAddress || agent.officeAddress || '',
          managementFeePercent: prop.managementFeePercent || agent.managementFeePercent || 0,
          lettingFeeNotes: prop.lettingFeeNotes || agent.lettingFeeNotes || '',
          renewalFeeNotes: prop.renewalFeeNotes || agent.renewalFeeNotes || '',
          inspectionFeeNotes: prop.inspectionFeeNotes || agent.inspectionFeeNotes || '',
          agentNotes: prop.agentNotes || agent.notes || '',
          effectiveFrom: prop.agentEffectiveFrom || agent.effectiveFrom || '',
          effectiveTo: prop.agentEffectiveTo || agent.effectiveTo || ''
        });
      }
    });
    return agents;
  },[activeProperties]);

  const handleAddProperty=async (data)=> {
    const result=await demoRepository.addProperty(data);
    if (!result?.success) {
      showToast(result?.error || 'Property save failed.','error');
      return result;
    }
    showToast(
      result.mode==='pocketbase' ? 'Property saved to PocketBase.' : 'Property saved to demo data.',
      'success'
    );
    // Keep the wizard open after a successful save so the user can review
    // the confirmation screen and choose when to close it.
    setRefreshTrigger(prev=> prev + 1);
    return result;
  };

  const handlePropertyImageUpload=async (property,event)=> {
    event.stopPropagation();
    const input=event.target;
    const incomingFiles=Array.from(input.files || []);
    input.value='';
    if (!incomingFiles.length) return;

    if (!pocketbaseService.isEnabled || demoRepository.isManualDemoMode?.()) {
      showToast('Property image upload requires the connected PocketBase data source.','error');
      return;
    }

    const allowedTypes=new Set(['image/jpeg','image/png','image/webp']);
    const existingCount=Array.isArray(property.propertyImages) ? property.propertyImages.length : 0;
    const availableSlots=Math.max(0,10-existingCount);
    const validFiles=incomingFiles.filter(file=> allowedTypes.has(file.type) && file.size <=10*1024*1024).slice(0,availableSlots);

    if (!validFiles.length) {
      showToast(availableSlots===0 ? 'This property already has the maximum of 10 images.' : 'Use JPEG, PNG or WebP images up to 10 MB each.','error');
      return;
    }

    setUploadingImagePropertyId(property.id);
    try {
      const result=await pocketbaseService.updateRecord('properties',property.id,{'propertyImages+': validFiles});
      if (!result?.success) throw new Error(result?.error || 'Image upload failed.');
      const refreshResult=await demoRepository.refreshFromPocketBase({force: true,silent: true,notify: true});
      if (!refreshResult?.success) throw new Error(refreshResult?.error || 'Image uploaded, but the property refresh failed.');
      showToast(validFiles.length===1 ? 'Property image uploaded.' : `${validFiles.length} property images uploaded.`,'success');
      setRefreshTrigger(prev=> prev + 1);
    } catch (error) {
      showToast(error?.message || 'Property image upload failed.','error');
    } finally {
      setUploadingImagePropertyId(null);
    }
  };

  const openImageEditor=(property,event)=> {
    event?.stopPropagation?.();
    const imageUrl=getPropertyHeroUrl(property);
    if (!imageUrl) {
      showToast('Upload a property image before adjusting the dashboard framing.','error');
      return;
    }
    const display=getCardImageDisplay(property);
    setImageEditor({
      propertyId: property.id,
      propertyName: property.name || 'Property',
      imageUrl,
      x: display.x,
      y: display.y,
      zoom: display.zoom,
      saving: false
    });
  };

  const handleImagePointerDown=(event)=> {
    if (!imageEditor) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    imageDragRef.current={
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: imageEditor.x,
      y: imageEditor.y,
      width: Math.max(1,event.currentTarget.clientWidth),
      height: Math.max(1,event.currentTarget.clientHeight)
    };
  };

  const handleImagePointerMove=(event)=> {
    const drag=imageDragRef.current;
    if (!drag || drag.pointerId!==event.pointerId) return;
    const dx=event.clientX-drag.clientX;
    const dy=event.clientY-drag.clientY;
    setImageEditor(prev=> prev ? {
      ...prev,
      x: clamp(drag.x-(dx/drag.width)*100,0,100),
      y: clamp(drag.y-(dy/drag.height)*100,0,100)
    } : prev);
  };

  const handleImagePointerUp=(event)=> {
    if (imageDragRef.current?.pointerId===event.pointerId) imageDragRef.current=null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleSaveImageFraming=async ()=> {
    if (!imageEditor) return;
    if (!pocketbaseService.isEnabled || demoRepository.isManualDemoMode?.()) {
      showToast('Dashboard image framing requires the connected PocketBase data source.','error');
      return;
    }

    const property=demoRepository.getPropertyById(imageEditor.propertyId);
    if (!property) {
      showToast('Property could not be found.','error');
      return;
    }

    setImageEditor(prev=> prev ? {...prev,saving: true} : prev);
    try {
      const metadata=getSafeMetadata(property);
      const cardImage={
        x: Math.round(clamp(imageEditor.x,0,100)*10)/10,
        y: Math.round(clamp(imageEditor.y,0,100)*10)/10,
        zoom: Math.round(clamp(imageEditor.zoom,1,1.6)*100)/100
      };
      const result=await pocketbaseService.updateRecord('properties',property.id,{
        metadata: {...metadata,cardImage}
      });
      if (!result?.success) throw new Error(result?.error || 'Image framing save failed.');
      const refreshResult=await demoRepository.refreshFromPocketBase({force: true,silent: true,notify: true});
      if (!refreshResult?.success) throw new Error(refreshResult?.error || 'Image framing saved, but the property refresh failed.');
      setImageEditor(null);
      setRefreshTrigger(prev=> prev + 1);
      showToast('Dashboard image framing saved.','success');
    } catch (error) {
      showToast(error?.message || 'Dashboard image framing save failed.','error');
      setImageEditor(prev=> prev ? {...prev,saving: false} : prev);
    }
  };

  const columns=[
    'Property','Address','Type','Market Value','Acquisition Cost','Loan Bal','Interest Rate','Monthly Installment','Anticipated Monthly Net Rent','Indicative Cash Flow','Agent Fees / Rate','Equity','Recorded Cash Flow'
  ];

  const renderRow=(prop)=> {
    const summary=calculations.getPropertySummary(prop.id);
    const loanMetrics=calculations.getPropertyLoanMetrics(prop.id);
    const rentMetrics=propertyRentMetricsById.get(String(prop.id)) || calculations.getPropertyAnticipatedRentMetrics(prop.id);
    const display=getCardImageDisplay(prop);
    const thumbUrl=getPropertyThumbUrl(prop);
    const installmentValue=loanMetrics.balance<=0 ? calculations.formatCurrencyAUD(0) : loanMetrics.installmentComplete ? calculations.formatCurrencyAUD(loanMetrics.monthlyInstallment) : '—';
    const rentValue=rentMetrics.rentComplete ? calculations.formatCurrencyAUD(rentMetrics.monthlyNetRent) : '—';
    const agentFeeValue=rentMetrics.feeComplete ? calculations.formatCurrencyAUD(rentMetrics.monthlyAgentFee) : '—';
    const agentFeeRate=rentMetrics.feeComplete && rentMetrics.effectiveAgentFeePercent!==null
      ? `${rentMetrics.effectiveAgentFeePercent.toFixed(2)}% effective`
      : 'Rate unavailable';
    const financial=getPropertyFinancialDetails(prop);
    const ownerOccupied=isOwnerOccupiedProperty(prop,financial.loanType);
    const indicativePosition=calculations.getPropertyIndicativeCashFlowPosition(prop.id);
    const nextContractedPosition=!ownerOccupied ? calculations.getPropertyNextContractedPosition(prop.id) : null;
    const indicativeValue=ownerOccupied
      ? 'PPR'
      : indicativePosition.complete
        ? calculations.formatCurrencyAUD(indicativePosition.monthlyCashFlow)
        : '—';
    const indicativeAnnual=ownerOccupied || !indicativePosition.complete
      ? ''
      : `${calculations.formatCurrencyAUD(indicativePosition.annualCashFlow)} / year`;
    const indicativeClass=indicativePosition.classification==='positive'
      ? 'text-emerald-700'
      : indicativePosition.classification==='negative'
        ? 'text-red-700'
        : 'text-slate-700';
    return [
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-10 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {thumbUrl ? <img src={thumbUrl} alt="" className="h-full w-full object-cover" style={{objectPosition: `${display.x}% ${display.y}%`,transform: `scale(${display.zoom})`,transformOrigin: `${display.x}% ${display.y}%`}} /> : <span className="text-[9px] text-slate-400">No image</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="break-words font-semibold leading-4 text-slate-900">{prop.name}</div>
          <div className="mt-1 flex flex-wrap gap-1" onClick={(e)=> e.stopPropagation()}>
            <label className={`inline-flex cursor-pointer rounded-md border px-1.5 py-0.5 text-[9px] font-medium transition-colors ${uploadingImagePropertyId===prop.id ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
              {uploadingImagePropertyId===prop.id ? 'Uploading...' : (prop.imageUrl ? 'Add image' : 'Upload image')}
              <input type="file" multiple accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" disabled={uploadingImagePropertyId===prop.id} onChange={(e)=> handlePropertyImageUpload(prop,e)} className="hidden" />
            </label>
            {prop.imageUrl && (
              <button type="button" onClick={(e)=> openImageEditor(prop,e)} className="rounded-md border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-700 transition-colors hover:bg-blue-100">
                Adjust
              </button>
            )}
          </div>
        </div>
      </div>,
      <div className="max-w-full whitespace-normal break-words text-[10px] leading-4 text-slate-500" title={`${prop.address},${prop.suburb}`}>{`${prop.address},${prop.suburb} ${prop.state}`}</div>,
      <span className="break-words">{prop.propertyType}</span>,
      <span className="whitespace-nowrap">{calculations.formatCurrencyAUD(prop.currentMarketValue)}</span>,
      <span className="whitespace-nowrap">{calculations.formatCurrencyAUD(summary.totalAcquisitionCost)}</span>,
      <span className="whitespace-nowrap">{calculations.formatCurrencyAUD(loanMetrics.balance)}</span>,
      <div className="whitespace-nowrap font-medium text-slate-700">{loanMetrics.interestRate===null ? '—' : `${loanMetrics.interestRate.toFixed(2)}%`}</div>,
      <div className="font-semibold leading-4 text-slate-800">{installmentValue}</div>,
      <div className={rentMetrics.rentComplete ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}>{rentValue}</div>,
      <div className={`min-w-[112px] ${indicativeClass}`}>
        <div className="whitespace-nowrap font-semibold">{indicativeValue}</div>
        {ownerOccupied ? <div className="mt-0.5 text-[9px] text-blue-600">Owner occupied</div> : indicativePosition.complete ? <><div className="mt-0.5 text-[9px] text-slate-500">{indicativeAnnual}</div><div className="mt-1 text-[9px] font-semibold">{indicativePosition.classification==='positive' ? 'Positive cash flow' : indicativePosition.classification==='negative' ? 'Negative cash flow' : 'Neutral cash flow'}</div></> : <div className="mt-0.5 text-[9px] text-amber-700">Rent or loan data incomplete</div>}{!ownerOccupied && nextContractedPosition?.rent?.rentComplete && (nextContractedPosition.changedGrossRent || nextContractedPosition.changedNetRent || nextContractedPosition.changedCashFlow) && <div className="mt-1.5 border-t border-blue-100 pt-1 text-[9px] leading-3 text-blue-700">From {calculations.formatDateAU(nextContractedPosition.effectiveDate)}: {calculations.formatCurrencyAUD(nextContractedPosition.rent.monthlyNetRent)}/mo net</div>}
      </div>,
      <div data-sort-value={rentMetrics.feeComplete ? rentMetrics.monthlyAgentFee : 0}>
        <div className={rentMetrics.feeComplete ? 'whitespace-nowrap font-semibold text-rose-700' : 'font-semibold text-amber-700'}>{agentFeeValue}</div>
        <div className="mt-0.5 text-[9px] leading-3 text-slate-500">{agentFeeRate}</div>
      </div>,
      <div className="font-medium">{calculations.formatCurrencyAUD(summary.equity)}</div>,
      <div className={summary.netCashFlow >=0 ? 'whitespace-nowrap font-medium text-emerald-600' : 'whitespace-nowrap font-medium text-red-600'}>{calculations.formatCurrencyAUD(summary.netCashFlow)}</div>
    ];
  };

  const renderPropertyCard=(prop)=> {
    const summary=calculations.getPropertySummary(prop.id);
    const loanMetrics=calculations.getPropertyLoanMetrics(prop.id);
    const rentMetrics=propertyRentMetricsById.get(String(prop.id)) || calculations.getPropertyAnticipatedRentMetrics(prop.id);
    const display=getCardImageDisplay(prop);
    const thumbUrl=getPropertyThumbUrl(prop);
    const financial=getPropertyFinancialDetails(prop);
    const ownerOccupied=isOwnerOccupiedProperty(prop,financial.loanType);
    const indicativePosition=calculations.getPropertyIndicativeCashFlowPosition(prop.id);
    const nextContractedPosition=!ownerOccupied ? calculations.getPropertyNextContractedPosition(prop.id) : null;
    const indicativeTone=indicativePosition.classification==='positive'
      ? {label: 'Positive cash flow',box: 'border-emerald-200 bg-emerald-50',text: 'text-emerald-800'}
      : indicativePosition.classification==='negative'
        ? {label: 'Negative cash flow',box: 'border-red-200 bg-red-50',text: 'text-red-800'}
        : indicativePosition.classification==='neutral'
          ? {label: 'Neutral cash flow',box: 'border-slate-200 bg-slate-50',text: 'text-slate-700'}
          : {label: 'Projection incomplete',box: 'border-amber-200 bg-amber-50',text: 'text-amber-800'};
    const metric=(label,value,valueClass='text-slate-900')=> (
      <div className="min-w-0 rounded-lg bg-slate-50 px-3 py-2">
        <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
        <div className={`mt-1 break-words text-sm font-bold ${valueClass}`}>{value}</div>
      </div>
    );
    return (
      <article key={prop.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <button type="button" onClick={()=> navigate(`/properties/${prop.id}`)} className="block w-full text-left">
          <div className="flex items-center gap-3 border-b border-slate-100 p-3">
            <div className="flex h-14 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
              {thumbUrl ? <img src={thumbUrl} alt="" className="h-full w-full object-cover" style={{objectPosition: `${display.x}% ${display.y}%`,transform: `scale(${display.zoom})`,transformOrigin: `${display.x}% ${display.y}%`}} /> : <span className="text-[9px] text-slate-400">No image</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-slate-900">{prop.name}</div>
                  <div className="mt-0.5 break-words text-[11px] leading-4 text-slate-500">{`${prop.address || ''}${prop.suburb ? ', '+prop.suburb : ''} ${prop.state || ''}`}</div>
                </div>
                <Badge label={prop.status} status={prop.status} />
              </div>
              <div className="mt-1 text-[10px] font-semibold text-amber-700">{ownerOccupied ? 'Primary residence / PPR' : 'Investment property'}</div>
            </div>
          </div>
        </button>

        <div className="grid grid-cols-2 gap-2 p-3">
          {metric('Market Value',calculations.formatCurrencyAUD(prop.currentMarketValue))}
          {metric('Loan Balance',calculations.formatCurrencyAUD(loanMetrics.balance))}
          {metric('Interest Rate',loanMetrics.interestRate===null ? '—' : `${loanMetrics.interestRate.toFixed(2)}%`)}
          {metric('Monthly Installment',loanMetrics.balance<=0 ? calculations.formatCurrencyAUD(0) : loanMetrics.installmentComplete ? calculations.formatCurrencyAUD(loanMetrics.monthlyInstallment) : '—',loanMetrics.installmentComplete ? 'text-slate-900' : 'text-amber-700')}
          {metric('Anticipated Gross Rent',rentMetrics.rentComplete ? `${calculations.formatCurrencyAUD(rentMetrics.monthlyGrossRent)}/mo · ${calculations.formatCurrencyAUD(rentMetrics.annualGrossRent)}/yr` : '—',rentMetrics.rentComplete ? 'text-cyan-700' : 'text-amber-700')}
          {metric('Anticipated Net Rent',rentMetrics.rentComplete ? `${calculations.formatCurrencyAUD(rentMetrics.monthlyNetRent)}/mo · ${calculations.formatCurrencyAUD(rentMetrics.annualNetRent)}/yr` : '—',rentMetrics.rentComplete ? 'text-emerald-700' : 'text-amber-700')}
          {metric('Agent Fees / Effective Rate',rentMetrics.feeComplete ? `${calculations.formatCurrencyAUD(rentMetrics.monthlyAgentFee)} / month · ${rentMetrics.effectiveAgentFeePercent?.toFixed(2) || '0.00'}%` : '—',rentMetrics.feeComplete ? 'text-rose-700' : 'text-amber-700')}
          {metric('Equity',calculations.formatCurrencyAUD(summary.equity),'text-emerald-700')}
          {metric('LVR',calculations.formatPercentage(summary.lvr,false))}
          {metric('Occupancy',calculations.formatPercentage(summary.occupancyRate,false))}
          {metric('Recorded Cash Flow',calculations.formatCurrencyAUD(summary.netCashFlow),summary.netCashFlow >=0 ? 'text-emerald-700' : 'text-red-600')}
        </div>

        {!ownerOccupied && (
          <div className="px-3 pb-3">
            <div className={`rounded-xl border p-3 ${indicativeTone.box}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">Indicative investment cash flow</div>
                  <div className={`mt-1 text-base font-bold ${indicativeTone.text}`}>
                    {indicativePosition.complete ? `${indicativePosition.monthlyCashFlow >= 0 ? '+' : '-'}${calculations.formatCurrencyAUD(Math.abs(indicativePosition.monthlyCashFlow))} / month` : 'Calculation incomplete'}
                  </div>
                  {indicativePosition.complete && <div className="mt-0.5 text-[11px] font-semibold text-slate-600">{`${indicativePosition.annualCashFlow >= 0 ? '+' : '-'}${calculations.formatCurrencyAUD(Math.abs(indicativePosition.annualCashFlow))} / year`}</div>}
                </div>
                <span className={`rounded-full border border-current/10 bg-white/70 px-2.5 py-1 text-[10px] font-bold ${indicativeTone.text}`}>{indicativeTone.label}</span>
              </div>
              {indicativePosition.complete ? (
                <div className="mt-2 text-[10px] leading-4 text-slate-600">
                  {calculations.formatCurrencyAUD(indicativePosition.rent.monthlyNetRent)} anticipated net rent − {calculations.formatCurrencyAUD(indicativePosition.loan.monthlyInstallment)} monthly installment = {calculations.formatCurrencyAUD(indicativePosition.monthlyCashFlow)} indicative monthly cash flow.
                </div>
              ) : (
                <div className="mt-2 text-[10px] leading-4 text-amber-800">Complete the current rent schedule and loan rate/term details to calculate this projection.</div>
              )}
            </div>
            {nextContractedPosition?.rent?.rentComplete && (nextContractedPosition.changedGrossRent || nextContractedPosition.changedNetRent || nextContractedPosition.changedCashFlow) && <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-2 text-[10px] leading-4 text-blue-800"><span className="font-semibold">Next contracted position from {calculations.formatDateAU(nextContractedPosition.effectiveDate)}:</span> {calculations.formatCurrencyAUD(nextContractedPosition.rent.monthlyGrossRent)}/mo gross · {calculations.formatCurrencyAUD(nextContractedPosition.rent.monthlyNetRent)}/mo net</div>}<p className="mt-2 text-[9px] leading-4 text-slate-400">Indicative only. Council rates, water, strata, insurance, maintenance and other ownership costs are excluded unless they are recorded in the rent schedule deductions.</p>
          </div>
        )}

        {ownerOccupied && (
          <div className="px-3 pb-3">
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-[10px] leading-4 text-blue-800">
              Primary residence / PPR. Monthly installment is shown above, but this property is not labelled positive or negative investment cash flow.
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3 py-2.5" onClick={(event)=> event.stopPropagation()}>
          <label className={`inline-flex cursor-pointer rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold transition-colors ${uploadingImagePropertyId===prop.id ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
            {uploadingImagePropertyId===prop.id ? 'Uploading...' : (prop.imageUrl ? 'Add image' : 'Upload image')}
            <input type="file" multiple accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" disabled={uploadingImagePropertyId===prop.id} onChange={(event)=> handlePropertyImageUpload(prop,event)} className="hidden" />
          </label>
          {prop.imageUrl && <button type="button" onClick={(event)=> openImageEditor(prop,event)} className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-100">Adjust image</button>}
          <button type="button" onClick={()=> navigate(`/properties/${prop.id}`)} className="ml-auto rounded-lg bg-slate-900 px-3 py-1.5 text-[10px] font-semibold text-white hover:bg-slate-800">View Property</button>
        </div>
      </article>
    );
  };

  const agentColumns=['Agency','Property Manager','Email','Phone','Statement Email','Mgmt Fee %','Linked Property'];

  const renderAgentRow=(agent)=> {
    return [
      <div className="font-semibold text-slate-900">{agent.agencyName || '-'}</div>,
      agent.managerName || '-',
      agent.email || '-',
      agent.phone || '-',
      agent.statementEmail || '-',
      <div className="font-medium">{agent.managementFeePercent ? `${agent.managementFeePercent}%` : '-'}</div>,
      <button onClick={()=> navigate(`/properties/${agent.propertyId}`)} className="text-xs font-medium text-amber-600 hover:underline">{agent.propertyName}</button>
    ];
  };

  const renderAgentCard=(agent)=> (
    <article key={agent.propertyId} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-slate-900">{agent.agencyName || 'Managing agent'}</div>
          <div className="mt-0.5 text-xs text-slate-500">{agent.managerName || 'Manager not recorded'}</div>
        </div>
        <button type="button" onClick={()=> navigate(`/properties/${agent.propertyId}`)} className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-100">{agent.propertyName}</button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Email</div><div className="mt-1 break-all text-xs font-medium text-slate-700">{agent.email || '-'}</div></div>
        <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Phone</div><div className="mt-1 text-xs font-medium text-slate-700">{agent.phone || '-'}</div></div>
        <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Statement Email</div><div className="mt-1 break-all text-xs font-medium text-slate-700">{agent.statementEmail || '-'}</div></div>
        <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Management Fee</div><div className="mt-1 text-xs font-medium text-slate-700">{agent.managementFeePercent ? `${agent.managementFeePercent}%` : '-'}</div></div>
      </div>
    </article>
  );

  return (
    <div className="animate-fade-in">
      <PageHeader title="Properties" subtitle="Manage your property assets,rooms,and spaces." actionLabel="New Property" onAction={()=> setIsAddModalOpen(true)} />

      <Tabs tabs={[{id: 'properties',label: 'Properties'},{id: 'agents',label: 'Active Agents'}]} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab==='properties' && (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Total Loan Balance</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">{calculations.formatCurrencyAUD(portfolioLoanMetrics.totalLoanBalance)}</div>
              <div className="mt-1 text-[10px] leading-4 text-slate-500">
                Active portfolio{portfolioLoanMetrics.weightedInterestRate===null ? '' : ` · weighted rate ${portfolioLoanMetrics.weightedInterestRate.toFixed(2)}%`}
              </div>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">PPR Monthly Installment</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {calculations.formatCurrencyAUD(installmentSplit.ppr.amount)}{installmentSplit.ppr.incomplete > 0 ? '+' : ''}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${installmentSplit.ppr.incomplete > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {installmentSplit.ppr.incomplete > 0
                  ? `Plus ${installmentSplit.ppr.incomplete} PPR loan${installmentSplit.ppr.incomplete===1 ? '' : 's'} awaiting rate/term details`
                  : installmentSplit.ppr.propertyCount > 0 ? `${calculations.formatCurrencyAUD(installmentSplit.ppr.amount*12)} / year · current recorded rates` : 'No active PPR property'}
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Investment Monthly Installment</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {calculations.formatCurrencyAUD(installmentSplit.investment.amount)}{installmentSplit.investment.incomplete > 0 ? '+' : ''}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${installmentSplit.investment.incomplete > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {installmentSplit.investment.incomplete > 0
                  ? `Plus ${installmentSplit.investment.incomplete} investment loan${installmentSplit.investment.incomplete===1 ? '' : 's'} awaiting rate/term details`
                  : `${calculations.formatCurrencyAUD(installmentSplit.investment.amount*12)} / year · ${installmentSplit.investment.propertyCount} investment propert${installmentSplit.investment.propertyCount===1 ? 'y' : 'ies'}`}
              </div>
            </div>

            <div className="rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total Monthly Installment</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {calculations.formatCurrencyAUD(portfolioLoanMetrics.totalMonthlyInstallment)}{portfolioLoanMetrics.installmentComplete ? '' : '+'}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${portfolioLoanMetrics.installmentComplete ? 'text-slate-500' : 'text-amber-700'}`}>
                {portfolioLoanMetrics.installmentComplete
                  ? `${calculations.formatCurrencyAUD(portfolioLoanMetrics.totalMonthlyInstallment*12)} / year · PPR + investments`
                  : `Plus ${portfolioLoanMetrics.incompleteLoanCount} loan${portfolioLoanMetrics.incompleteLoanCount===1 ? '' : 's'} awaiting rate/term details`}
              </div>
            </div>

            <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700">Total Anticipated Gross Rent</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {calculations.formatCurrencyAUD(portfolioRentMetrics.anticipatedMonthlyGrossRent)}{portfolioRentMetrics.rentIncompleteProperties > 0 ? '+' : ''}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${portfolioRentMetrics.rentIncompleteProperties > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {portfolioRentMetrics.rentIncompleteProperties > 0
                  ? `Plus ${portfolioRentMetrics.rentIncompleteProperties} rental propert${portfolioRentMetrics.rentIncompleteProperties===1 ? 'y' : 'ies'} awaiting complete schedule data`
                  : portfolioRentMetrics.rentalPropertyCount > 0 ? `${calculations.formatCurrencyAUD(portfolioRentMetrics.anticipatedMonthlyGrossRent*12)} / year · before recorded schedule deductions` : 'No current rental schedules'}
              </div>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Total Anticipated Monthly Net Rent</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {calculations.formatCurrencyAUD(portfolioRentMetrics.anticipatedMonthlyNetRent)}{portfolioRentMetrics.rentIncompleteProperties > 0 ? '+' : ''}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${portfolioRentMetrics.rentIncompleteProperties > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {portfolioRentMetrics.rentIncompleteProperties > 0
                  ? `Plus ${portfolioRentMetrics.rentIncompleteProperties} rental propert${portfolioRentMetrics.rentIncompleteProperties===1 ? 'y' : 'ies'} awaiting complete deduction data`
                  : portfolioRentMetrics.rentalPropertyCount > 0 ? `${calculations.formatCurrencyAUD(portfolioRentMetrics.anticipatedMonthlyNetRent*12)} / year · after recorded schedule deductions` : 'No current rental schedules'}
              </div>
            </div>

            <div className="rounded-xl border border-rose-200 bg-rose-50/40 px-4 py-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Total Monthly Agent Fees</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {calculations.formatCurrencyAUD(portfolioRentMetrics.monthlyAgentFees)}{portfolioRentMetrics.feeIncompleteProperties > 0 ? '+' : ''}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${portfolioRentMetrics.feeIncompleteProperties > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {portfolioRentMetrics.feeIncompleteProperties > 0
                  ? `Plus ${portfolioRentMetrics.feeIncompleteProperties} rental propert${portfolioRentMetrics.feeIncompleteProperties===1 ? 'y' : 'ies'} awaiting fee data`
                  : portfolioRentMetrics.effectiveAgentFeePercent!==null ? `${portfolioRentMetrics.effectiveAgentFeePercent.toFixed(2)}% effective portfolio rate` : 'No current agent fees'}
              </div>
            </div>

            <div className={`rounded-xl border px-4 py-3 shadow-sm ${indicativePortfolioCashFlow.monthlyCashFlow >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wide ${indicativePortfolioCashFlow.monthlyCashFlow >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Indicative Investment Cash Flow</div>
              <div className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                {indicativePortfolioCashFlow.investmentPropertyCount===0 ? calculations.formatCurrencyAUD(0) : `${indicativePortfolioCashFlow.monthlyCashFlow >= 0 ? '+' : '-'}${calculations.formatCurrencyAUD(Math.abs(indicativePortfolioCashFlow.monthlyCashFlow))}`}{indicativePortfolioCashFlow.incomplete > 0 ? '+' : ''}
              </div>
              <div className={`mt-1 text-[10px] leading-4 ${indicativePortfolioCashFlow.incomplete > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                {indicativePortfolioCashFlow.incomplete > 0
                  ? `${indicativePortfolioCashFlow.incomplete} investment propert${indicativePortfolioCashFlow.incomplete===1 ? 'y needs' : 'ies need'} complete rent/loan data`
                  : `${indicativePortfolioCashFlow.annualCashFlow >= 0 ? '+' : '-'}${calculations.formatCurrencyAUD(Math.abs(indicativePortfolioCashFlow.annualCashFlow))} / year · ${indicativePortfolioCashFlow.positive} positive · ${indicativePortfolioCashFlow.negative} negative`}
              </div>
            </div>
          </div>

          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-[10px] leading-4 text-amber-900">
            <strong>Indicative cash-flow disclaimer:</strong> anticipated figures use the current rent schedule, deductions actually recorded in that schedule, and formula-driven loan installments at current recorded interest rates/terms. Council rates, water, strata, insurance, maintenance and other ownership costs that are not recorded are excluded, so these figures are high-level indicators rather than complete profitability or tax results. Recorded Cash Flow remains the historical cash result from transactions.
          </div>

          <FilterBar onSearch={setSearchTerm} onClear={()=> {setSearchTerm('');setStatusFilter('Active');}}>
            <select value={statusFilter} onChange={e=> setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500">
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
              <option value="Under Contract">Under Contract</option>
              <option value="Archived">Archived</option>
            </select>
          </FilterBar>

          <div className="hidden 2xl:block">
            <DataTable columns={columns} columnHelp={propertyColumnHelp} data={properties} renderRow={renderRow} onRowClick={(row)=> navigate(`/properties/${row.id}`)} emptyMessage="No properties found matching your criteria." />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:hidden">
            {properties.length > 0
              ? properties.map(renderPropertyCard)
              : <div className="md:col-span-2 rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-500">No properties found matching your criteria.</div>}
          </div>
        </>
      )}

      {activeTab==='agents' && (
        activeAgents.length > 0 ? (
          <>
            <div className="hidden 2xl:block">
              <DataTable columns={agentColumns} data={activeAgents} renderRow={renderAgentRow} emptyMessage="No active managing agents recorded yet." />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:hidden">
              {activeAgents.map(renderAgentCard)}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center p-12 text-center bg-white border border-dashed border-slate-300 rounded-xl">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656-.126-1.283-.356-1.857m0 0a5.404 5.404 0 017.288 0m0 0a5.404 5.404 0 00-7.288 0m0 0a3 3 0 00-5.356 1.857M12 10a4 4 0 100-8 4 4 0 000 8z"/></svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No Active Managing Agents</h3>
            <p className="text-sm text-slate-500 max-w-sm mb-6">No active managing agents recorded yet. Add agent details from a property.</p>
          </div>
        )
      )}

      <Modal isOpen={isAddModalOpen} onClose={()=> setIsAddModalOpen(false)} title="Add Property" maxWidth="max-w-6xl">
        <PropertyForm onSubmit={handleAddProperty} onCancel={()=> setIsAddModalOpen(false)} />
      </Modal>

      <Modal isOpen={!!imageEditor} onClose={()=> {if (!imageEditor?.saving) setImageEditor(null);}} title="Adjust Dashboard Property Image">
        {imageEditor && (
          <div className="space-y-5">
            <div>
              <p className="text-sm font-semibold text-slate-800">{imageEditor.propertyName}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Drag the image to choose which part of the property is visible on the Dashboard card. Use Zoom for a tighter or wider framing.</p>
            </div>

            <div
              className="relative h-56 w-full touch-none select-none overflow-hidden rounded-xl border border-slate-200 bg-slate-100 cursor-grab active:cursor-grabbing sm:h-64"
              onPointerDown={handleImagePointerDown}
              onPointerMove={handleImagePointerMove}
              onPointerUp={handleImagePointerUp}
              onPointerCancel={handleImagePointerUp}
            >
              <img
                src={imageEditor.imageUrl}
                alt="Dashboard card preview"
                draggable="false"
                className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                style={{
                  objectPosition: `${imageEditor.x}% ${imageEditor.y}%`,
                  transform: `scale(${imageEditor.zoom})`,
                  transformOrigin: `${imageEditor.x}% ${imageEditor.y}%`
                }}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/95 to-transparent px-4 pb-4 pt-12">
                <div className="text-lg font-bold text-slate-900">{imageEditor.propertyName}</div>
                <div className="mt-1 text-xs text-slate-500">Dashboard card preview</div>
              </div>
              <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-slate-900/65 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-sm">Drag to reposition</div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block">
                <div className="mb-1 flex items-center justify-between text-xs font-medium text-slate-600"><span>Zoom</span><span>{Math.round(imageEditor.zoom*100)}%</span></div>
                <input type="range" min="1" max="1.6" step="0.01" value={imageEditor.zoom} onChange={(e)=> setImageEditor(prev=> prev ? {...prev,zoom: Number(e.target.value)} : prev)} className="w-full accent-amber-600" />
              </label>
              <label className="block">
                <div className="mb-1 flex items-center justify-between text-xs font-medium text-slate-600"><span>Horizontal</span><span>{Math.round(imageEditor.x)}%</span></div>
                <input type="range" min="0" max="100" step="1" value={imageEditor.x} onChange={(e)=> setImageEditor(prev=> prev ? {...prev,x: Number(e.target.value)} : prev)} className="w-full accent-amber-600" />
              </label>
              <label className="block">
                <div className="mb-1 flex items-center justify-between text-xs font-medium text-slate-600"><span>Vertical</span><span>{Math.round(imageEditor.y)}%</span></div>
                <input type="range" min="0" max="100" step="1" value={imageEditor.y} onChange={(e)=> setImageEditor(prev=> prev ? {...prev,y: Number(e.target.value)} : prev)} className="w-full accent-amber-600" />
              </label>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-between">
              <button type="button" disabled={imageEditor.saving} onClick={()=> setImageEditor(prev=> prev ? {...prev,x: 50,y: 45,zoom: 1} : prev)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">Reset framing</button>
              <div className="flex gap-2 sm:justify-end">
                <button type="button" disabled={imageEditor.saving} onClick={()=> setImageEditor(null)} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 sm:flex-none">Cancel</button>
                <button type="button" disabled={imageEditor.saving} onClick={handleSaveImageFraming} className="flex-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60 sm:flex-none">{imageEditor.saving ? 'Saving...' : 'Save card framing'}</button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PropertiesPage;